<template>
    <div class="ImageSelector__wrap">
        <div
            class="ImageSelector__heading"
            v-if="type !== 'hero-selector' && !hideLabel"
        >
            <span class="ImageSelector__label">{{ name }}</span>
            <div class="ImageSelector__help">
                <HelpModal
                    v-if="helpText"
                    :text="helpText"
                    :title="helpTitle"
                />
            </div>
        </div>
        <div
            class="ImageSelector"
            v-if="namespace"
        >
            <div class="ImageSelector__uploader">
                <!-- Add selected image ID as query var to force reload -->
                <v-img
                    :src="selectedImageSrc + '?' + selectedImage"
                    aspect-ratio="1"
                    cover
                    class="ImageSelector__preview"
                >
                    <template #placeholder>
                        <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                        >
                            <v-progress-circular
                                indeterminate
                                color="primary"
                            />
                        </v-row>
                    </template>
                </v-img>
            </div>

            <div
                v-if="uploaderInstructionText"
                class="ImageSelector__instruction"
            >
                {{ uploaderInstructionText }}
            </div>

            <div class="ImageSelector__buttons">
                <v-btn
                    color="primary"
                    class="ImageSelector__trigger"
                    @click="openSelector('stockMedia')"
                    v-if="showSelectButton === 'yes'"
                >
                    <v-icon
                        v-if="uploadType === 'image'"
                        left
                        small
                    >
                        fas fa-image
                    </v-icon>
                    <v-icon
                        v-else
                        left
                        small
                    >
                        fas fa-file-pdf
                    </v-icon>
                    <span>Select <span v-if="uploadType === 'image'">Image</span><span v-else>PDF</span></span>
                </v-btn>
                <v-btn
                    v-if="allowUpload && showUploadButton === 'yes'"
                    color="secondary"
                    class="ImageSelector__trigger"
                    @click="openSelector('upload')"
                >
                    <v-icon
                        left
                        small
                    >
                        fas fa-cloud-upload-alt
                    </v-icon>
                    <span>Upload <span v-if="uploadType === 'pdf'">Pdf</span><span v-else>Image</span></span>
                </v-btn>
            </div>

            <v-dialog
                v-model="showMediaSelector"
                :key="namespace"
                eager
                scrollable
            >
                <v-card
                    flat
                    class="d-flex flex-column"
                >
                    <v-overlay
                        :absolute="true"
                        v-model="overlay"
                        color="#f3f6f9"
                        opacity="0.9"
                    >
                        <v-container
                            fluid
                            fill-height
                        >
                            <v-layout
                                justify-center
                                align-center
                            >
                                <v-progress-circular
                                    indeterminate
                                    color="primary"
                                />
                            </v-layout>
                        </v-container>
                    </v-overlay>
                    <v-card-title>
                        <v-tabs
                            v-model="activeTab"
                            :key="namespace"
                            :vertical="$vuetify.breakpoint.xsOnly"
                        >
                            <v-tab
                                key="upload"
                                href="#upload"
                                v-if="allowUpload"
                            >
                                <span v-if="uploadType === 'image'">
                                    Upload Image
                                </span>
                                <span v-else>
                                    Upload File
                                </span>
                            </v-tab>

                            <v-tab
                                key="stockMedia"
                                href="#stockMedia"
                                v-if="loadStockLibrary === 'yes'"
                            >
                                <span v-if="uploadType === 'image'">
                                    Stock Images
                                </span>
                                <span v-else>
                                    Stock Files
                                </span>
                            </v-tab>

                            <v-tab
                                key="userMedia"
                                href="#userMedia"
                                v-if="loadUserLibrary"
                            >
                                <span v-if="uploadType === 'image'">
                                    My Images
                                </span>
                                <span v-else>
                                    My Files
                                </span>
                            </v-tab>
                        </v-tabs>
                    </v-card-title>
                    <v-card-text
                        class="MediaSelector__wrap"
                        style="height: 80vh"
                    >
                        <v-tabs-items
                            v-model="activeTab"
                            transition="fade-transition"
                            :key="namespace"
                        >
                            <!--
                        Media Uploader
                        -->
                            <v-tab-item
                                key="upload"
                                value="upload"
                                class="upload"
                                v-if="allowUpload"
                                eager
                            >
                                <ImageUploader
                                    v-if="namespace"
                                    :namespace="namespace"
                                    :field-location="fieldLocation"
                                    :upload-type="uploadType"
                                />
                            </v-tab-item>

                            <!--
                        Media Selector
                        -->
                            <v-tab-item
                                key="stockMedia"
                                value="stockMedia"
                                v-if="loadStockLibrary === 'yes'"
                                eager
                            >
                                <StockSelector
                                    v-if="namespace"
                                    :namespace="namespace"
                                    :selected-image-id="selectedImage"
                                    :preloaded-stock-media="stockMedia"
                                    :upload-type="uploadType"
                                />
                            </v-tab-item>

                            <!--
                        User Library
                        -->
                            <v-tab-item
                                key="userMedia"
                                value="userMedia"
                                v-if="loadUserLibrary"
                                eager
                            >
                                <UserLibrarySelector
                                    v-if="namespace"
                                    :namespace="namespace"
                                    :selected-image-id="selectedImage"
                                    :preloaded-user-media="userMedia"
                                    :upload-type="uploadType"
                                />
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            text
                            @click="showMediaSelector = !showMediaSelector"
                        >
                            Cancel
                        </v-btn>
                        <v-btn
                            color="primary"
                            @click="showMediaSelector = !showMediaSelector"
                        >
                            Save & Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import StockSelector from '@/components/ImageSelector/StockSelector';
import ImageUploader from '@/components/ImageSelector/ImageUploader';
import store from '@/store/store';
import ImageSelectorModule from '@/store/Modules/common/imageSelector.store';
import UserLibrarySelector from '@/components/ImageSelector/UserLibrarySelector';
import { EventBus } from '@/store/eventBus';
import HelpModal from '@/components/HelpModal';

export default {
    components: { HelpModal, UserLibrarySelector, ImageUploader, StockSelector },
    props: {
        hideLabel: {
            type: Boolean
        },

        fieldNamespace: {
            type: String,
            required: true
        },

        selectedImageId: {
            type: String,
            required: true
        },

        loadUserLibrary: {
            type: Boolean
        },

        userMedia: {
            type: Array
        },

        loadStockLibrary: {
            type: String
        },

        stockMedia: {
            type: Array
        },

        defaultTab: {
            type: Number
        },

        fieldLocation: {
            type: String
        },

        media: {
            type: Array
        },

        allowUpload: {
            type: Boolean
        },

        uploadType: {
            type: String,
            required: true
        }
    },

    computed: {
        ...mapState('auth', ['user']),

        name() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].name;
            } else {
                return [];
            }
        },

        type() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].type;
            } else {
                return [];
            }
        },

        helpText() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].options.helpText;
            } else {
                return [];
            }
        },

        helpTitle() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].options.helpTitle;
            } else {
                return [];
            }
        },

        population() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].population;
            } else {
                return [];
            }
        },

        submission() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].submission;
            } else {
                return [];
            }
        },

        showUploadButton() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].options.showUploadButton;
            } else {
                return 'no';
            }
        },

        showSelectButton() {
            if (store.state[this.fieldNamespace]) {
                return store.state[this.fieldNamespace].options.showSelectButton;
            } else {
                return 'yes';
            }
        },

        uploaderInstructionText: {
            get: function() {
                if (store.state[this.fieldNamespace]) {
                    return store.state[this.fieldNamespace].options.uploaderInstructionText;
                } else {
                    return '';
                }
            },
            set: function(value) {
                store.commit(`${this.fieldNamespace}/setUploaderInstructionText`, value);
            }
        },

        showMediaSelector: {
            get: function() {
                return store.state[this.namespace].showMediaSelector;
            },
            set: function(value) {
                store.commit(`${this.namespace}/toggleMediaSelector`, value);
            }
        },

        selectedImage: {
            get: function() {
                return store.state[this.namespace].selectedImage;
            },
            set: function(value) {
                store.commit(`${this.namespace}/setSelectedImage`, value);
            }
        },

        selectedImageSrc: {
            get: function() {
                return store.state[this.namespace].selectedImageSrc;
            },
            set: function(value) {
                store.commit(`${this.namespace}/setSelectedImageSrc`, value);
            }
        },

        activeTab: {
            get: function() {
                return store.state[this.namespace].activeTab;
            },
            set: function(value) {
                store.commit(`${this.namespace}/setActiveTab`, value);
            }
        },

        overlay: {
            get: function() {
                return store.state[this.namespace].overlay;
            },
            set: function(value) {
                store.commit(`${this.namespace}/toggleOverlay`, value);
            }
        }
    },

    data() {
        return {
            storageURL: this.$storageURL,
            namespace: ''
        };
    },

    created() {
        if (this.fieldNamespace) {
            this.registerStoreModule()
                .then((namespace) => {
                    this.loadData(namespace)
                        .then(() => {
                            this.setImage();
                            EventBus.$on(`${this.namespace}-selected`, (imageData) => {
                                if (store.state[this.fieldNamespace]) {
                                    store.commit(
                                        `${this.fieldNamespace}/setEntry`,
                                        { selectedImage: imageData.selectedImageId }
                                    );
                                }
                            });
                        });
                });
        }
    },

    beforeDestroy() {
        store.unregisterModule(this.namespace);
    },

    methods: {
        setImage() {
            let img = 0;

            if (this.submission && this.submission.selectedImage) {
                img = this.submission.selectedImage;
            } else if (this.population && this.population.selectedImage) {
                img = this.population.selectedImage;
            } else if (this.selectedImageId) { // Passed in as prop
                img = this.selectedImageId;
            }
            if (this.namespace && img) {
                store.dispatch(`${this.namespace}/setSelectedImage`, img)
                    .then(() => {
                        store.dispatch(`${this.namespace}/getSelectedImage`);
                    });
            }

            if (store.state[this.fieldNamespace]) {
                store.commit(`${this.fieldNamespace}/setEntry`, { selectedImage: img });
            }
        },

        registerStoreModule() {
            return new Promise((resolve) => {
                const ns = `imageSelector-${this.fieldNamespace}`;

                if ((store && store.state && store.state[ns])) {
                    store.unregisterModule(ns);
                }

                store.registerModule(ns, ImageSelectorModule);
                resolve(ns);
            });
        },

        loadData(ns) {
            return new Promise((resolve) => {
                this.namespace = ns;
                store.commit(`${this.namespace}/setUploadType`, this.uploadType);
                resolve();
            });
        },

        openSelector(tabToSelect) {
            if (tabToSelect === 'stockMedia') {
                if (this.loadStockLibrary === 'yes') {
                    this.activeTab = 'stockMedia';
                    this.showMediaSelector = true;
                    return;
                }

                if (this.loadUserLibrary) {
                    this.activeTab = 'userMedia';
                    this.showMediaSelector = true;
                    return;
                }
            }

            this.activeTab = tabToSelect;
            this.showMediaSelector = true;
        }
    }
};
</script>

<style lang="scss">
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
{
    opacity: 0;
}

.ImageSelector__wrap {
    flex: 0 0 100%;
    width: 100%;
}

.ImageSelector__heading {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 12px;
}

.ImageSelector__label {
    font-weight: 500;
}

.ImageSelector__help {
    flex: 0 0 auto;
    width: auto;
    margin-top: -1px;
    margin-left: auto;
}

.ImageSelector {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
    border-radius: $border-radius-root;
    border: 1px solid rgba(black, 0.38);
    margin-bottom: 24px;
    padding: 12px;

    @media (min-width: 1200px) {
        flex-flow: row nowrap;
        align-content: center;
        align-items: center;
    }
}

.ImageSelector__buttons {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
    margin-left: auto;

    @media (min-width: 1200px) {
        align-content: flex-start;
        align-items: flex-start;
        flex: 0 0 30%;
        width: 30%;
    }
}

.ImageSelector__uploader {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 12px;

    .v-image {
        margin-bottom: 0 !important;
    }

    @media (min-width: 700px) {
        flex: 0 0 20%;
        width: 20%;
    }

    @media (min-width: 1200px) {
        flex: 0 0 84px;
        width: 84px;
        margin-bottom: 0;
    }
}

.ImageSelector__instruction {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 12px;

    @media (min-width: 700px) {
        flex: 0 0 80%;
        width: 80%;
        margin-bottom: 0;
        padding-left: 12px;
    }

    @media (min-width: 1200px) {
        flex: 1 1 auto;
        width: auto;
        padding-right: 12px;
    }
}

.ImageSelector__preview {
    display: flex;
    flex-flow: row wrap;
    flex: 0 0 100%;
    width: 100%;
    margin-bottom: 12px;
}

.ImageSelector__trigger {
    flex: 0 0 100%;
    width: 100%;
}

.ImageSelector__trigger + .ImageSelector__trigger {
    margin-top: 12px;
}

.MediaSelector__no-images {
    flex: 0 0 100%;
    width: 100%;
    height: 70vh;
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    align-content: center;
    align-items: center;

    p {
        flex: 0 0 100%;
        width: 100%;
        text-align: center;
    }
}

.MediaSelector__wrap {
    padding-top: 24px;
    display: flex;
    flex-flow: column wrap;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    flex: 1 0 100%;
    width: 100%;

    .v-window {
        flex: 1 0 100%;
        width: 100%;
        display: flex;
        flex-flow: column wrap;
        overflow: scroll;
    }

    .v-window__container {
        flex: 1 0 100%;
        width: 100%;
        display: flex;
        flex-flow: column wrap;
    }

    .v-window-item.upload,
    .v-window-item.upload > span {
        display: flex;
        flex-flow: row nowrap;
        flex: 1 0 100%;
        width: 100%;
        justify-content: center;
        align-content: center;
        align-items: center;
    }
}

.MediaSelector__selector {
    flex: 0 0 100%;
    width: 100%;
    display: grid;
    grid-gap: 18px;
    grid-template-columns: repeat(1, 1fr);
    list-style: none;
    margin: 0 0 24px !important;
    padding: 0 !important;

    @media (min-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: 1280px) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (min-width: 1440px) {
        grid-template-columns: repeat(5, 1fr);
    }

    @media (min-width: 1800px) {
        grid-template-columns: repeat(6, 1fr);
    }
}

.Media {
    position: relative;
    display: flex;
    flex-flow: column nowrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: flex-start;
    flex: 0 0 100%;
    width: 100%;
}

button.selected {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
    border-radius: $border-radius-root;
    background-color: rgba(black, 0.6);
    color: white;
    z-index: 5;
    opacity: 0;
    transition: opacity 0.2s linear;
    pointer-events: none;

    i {
        font-size: 4rem !important;
    }
}

.Media__label {
    position: relative;
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
    border-radius: $border-radius-root;
    background-color: white;
    cursor: pointer;
    box-shadow: 2px 2px 4px rgba(black, 0.1);
    transition: box-shadow 0.2s linear;

    .image-wrap {
        position: relative;
        flex: 0 0 100%;
        width: 100%;
        height: 0;
        padding-top: 56.25%;
        overflow: hidden;

        .image-inner {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center center;
        }
    }

    .image-filename {
        display: flex;
        flex-flow: row wrap;
        justify-content: flex-start;
        flex: 1 0 80%;
        width: 80%;
        padding: 12px;
        background-color: rgba(white, 0.8);
        word-break: break-all;
        font-weight: bold;
    }

    .delete {
        display: flex;
        flex-flow: row nowrap;
        justify-content: center;
        align-content: center;
        align-items: center;
        flex: 0 0 32px;
        width: 32px;
        color: #3c0018;
        z-index: 5;
    }
}

.Media__input {
    position: absolute;
    z-index: 0;
    cursor: pointer;
}

.Media__input:checked {
    & ~ button.selected {
        opacity: 1;
        transition: opacity 0.2s linear;
        pointer-events: auto;
    }

    & + label {
        box-shadow: 2px 2px 4px rgba(black, 0.6);
        transition: box-shadow 0.2s linear;
    }
}

.MediaSelector__uploader {
    position: relative;
    display: flex;
    flex-flow: row wrap;
    width: 100%;
    max-width: 320px;
    height: 320px;
}

.MediaSelector__uploader__input {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 1;
}

.MediaSelector__uploader__label {
    position: absolute;
    top: 12px;
    right: 12px;
    bottom: 12px;
    left: 12px;
    z-index: 2;
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    height: 100%;
    padding: 24px;
    border: 3px dashed #cecece;
    border-radius: 8px;
    background-color: #f4f4f4;
    cursor: pointer;
    font-size: 1.25rem;
    color: #888888;
    text-align: center;

    &.jsHasError {
        background-color: #3c0018;
        color: white;
    }

    .FileLabel {
        display: flex;
        flex-flow: row wrap;
        justify-content: center;
        flex: 0 0 100%;
        width: 100%;
    }

    .icon {
        margin-bottom: 18px !important;
    }

    .FileErrors {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 4;
        display: flex;
        flex-flow: row wrap;
        justify-content: center;
        align-content: center;
        align-items: center;
        padding: 24px;

        .icon {
            margin-bottom: 18px !important;
            color: white;
        }

        .text {
            display: flex;
            flex-flow: row wrap;
            flex: 0 0 100%;
            width: 100%;
            text-align: left;

            .Error {
                flex: 0 0 100%;
                width: 100%;
                margin-bottom: 16px;
            }
        }
    }
}
</style>
